import api from '@Services/api';
import { AxiosResponse } from 'axios';

import {
	AddUnitVideoImportPayload,
	AddUnitVideoImportResult,
	CreateVideoImportFolderPayload,
	CreateVideoImportFolderResult,
	CreateVideoImportPayload,
	FetchVideoImportsPayload,
	GetVideoImportPayload,
	InspectVideoImportFolderPayload,
	InspectVideoImportFolderResult,
	VideoImport,
	VideoImportCapabilities,
} from '../Types/VideoImports.types';

/**
 * Files-tab video imports. Every call carries course_id in the body — the
 * content-parent middleware scopes on it, and in lecture mode the
 * routeContentRequest interceptor reroutes the URL and injects
 * standalone_lecture_id on top.
 */
export class VideoImportsRequester {
	private static instance: VideoImportsRequester;
	static getInstance() {
		if (!VideoImportsRequester.instance) {
			VideoImportsRequester.instance = new VideoImportsRequester();
		}
		return VideoImportsRequester.instance;
	}
	baseURL = 'video-import';

	async create(
		payload: CreateVideoImportPayload
	): Promise<
		AxiosResponse<
			// A OneDrive FOLDER link auto-expands server-side into the folder shape.
			ApiResponse<{ import: VideoImport } | CreateVideoImportFolderResult>
		>
	> {
		return await api.post(`${this.baseURL}/create`, payload);
	}

	async createFolder(
		payload: CreateVideoImportFolderPayload
	): Promise<AxiosResponse<ApiResponse<CreateVideoImportFolderResult>>> {
		return await api.post(`${this.baseURL}/create-folder`, payload);
	}

	async inspectFolder(
		payload: InspectVideoImportFolderPayload
	): Promise<AxiosResponse<ApiResponse<InspectVideoImportFolderResult>>> {
		return await api.post(`${this.baseURL}/inspect-folder`, payload);
	}

	async fetch(
		payload: FetchVideoImportsPayload
	): Promise<AxiosResponse<ApiResponse<{ imports: VideoImport[] }>>> {
		// silent is a UI concern — never sent to the backend.
		const { silent, ...body } = payload;
		return await api.post(`${this.baseURL}/fetch`, body);
	}

	async get(
		payload: GetVideoImportPayload
	): Promise<AxiosResponse<ApiResponse<{ import: VideoImport }>>> {
		return await api.post(`${this.baseURL}/get`, payload);
	}

	async addUnit(
		payload: AddUnitVideoImportPayload
	): Promise<AxiosResponse<ApiResponse<AddUnitVideoImportResult>>> {
		return await api.post(`${this.baseURL}/add-unit`, payload);
	}

	async retry(
		payload: GetVideoImportPayload
	): Promise<AxiosResponse<ApiResponse<{ import: VideoImport }>>> {
		return await api.post(`${this.baseURL}/retry`, payload);
	}

	async deleteById(
		payload: GetVideoImportPayload
	): Promise<AxiosResponse<ApiResponse<{ import: VideoImport }>>> {
		return await api.delete(`${this.baseURL}/delete`, {
			data: payload,
		});
	}

	async providers(): Promise<
		AxiosResponse<ApiResponse<{ providers: VideoImportCapabilities }>>
	> {
		// Empty object on purpose: lecture mode injects standalone_lecture_id
		// into object bodies only, and the standalone scope middleware 422s
		// without it.
		return await api.post(`${this.baseURL}/providers`, {});
	}
}
